"use client";

import { createContext, useContext, useState } from "react";

import { useBrowserNativeTransitions } from "./browser-native-events";
import { useClickThroughTransitions } from "./click-through";
import type { SetFinishViewTransitionCallback } from "./types";

const ViewTransitionsContext = createContext<SetFinishViewTransitionCallback>(
  () => () => {}
);

/**
 * ViewTransitions component that enables View Transitions API for Next.js App Router.
 * This version is fully compatible with Next.js 16 and React 19.
 */
export function ViewTransitions({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [finishViewTransition, setFinishViewTransition] = useState<
    (() => void) | null
  >(null);

  const [prevFinishViewTransition, setPrevFinishViewTransition] =
    useState(finishViewTransition);

  if (finishViewTransition !== prevFinishViewTransition) {
    if (finishViewTransition) {
      finishViewTransition();
      setFinishViewTransition(null);
    }
    setPrevFinishViewTransition(finishViewTransition);
  }
  useBrowserNativeTransitions();
  useClickThroughTransitions();

  // No Suspense here: the root layouts render this above <html>, so a boundary
  // would hold the whole document and turn every redirect(), forbidden() or
  // error of a page into a client-rendered document that crashes the router.
  return (
    <ViewTransitionsContext.Provider value={setFinishViewTransition}>
      {children}
    </ViewTransitionsContext.Provider>
  );
}

/**
 * Hook to access the finish view transition callback from context.
 */
export function useSetFinishViewTransition(): SetFinishViewTransitionCallback {
  const context = useContext(ViewTransitionsContext);
  if (!context) {
    throw new Error(
      "useSetFinishViewTransition must be used within ViewTransitions"
    );
  }
  return context;
}
